/**
 * @fileoverview Lookup-baseline repair surface composed from the shared department status table.
 * @module system-web/components/dataRepair/LookupBaselineRepairTab
 *
 * Responsibilities:
 * - Describe the lookup-baseline repair and supply its lookup-specific table columns.
 * - Bind the shared table to lookup-baseline job type and rerun route.
 *
 * Dependencies:
 * - ../../hooks/useDepartmentRepairJobs for local-first state and rerun actions.
 * - ./DepartmentRepairStatusTable for shared status presentation.
 *
 * Architecture:
 * - All rows are derived from synced Dexie state; no direct API or database access occurs here.
 *
 * Notes:
 * - Lookup-specific surface may be removed with the transitional lookup-baseline repair after every environment reports zero failures.
 */
import React from 'react';
import DepartmentRepairStatusTable from './DepartmentRepairStatusTable';
import { useDepartmentRepairJobs } from '../../hooks/useDepartmentRepairJobs';

const JOB_TYPE = 'lookupBaselineRepair';
const REQUEST_PATH = '/api/system/data-repair/lookup-baselines/run';

/**
 * @component LookupBaselineRepairTab
 * Purpose: Review lookup-baseline outcomes and rerun departments that need attention.
 * Props: None.
 * Behavior: Supplies lookup counts to the shared table and exposes generic rerun actions.
 * Data Source: Local (synced Dexie background_jobs and tenants through useDepartmentRepairJobs).
 * Constraints: No typed confirmation; repair only adds missing baselines.
 * Performance: Counts, sorting, and interaction state are handled by the shared table.
 */
export default function LookupBaselineRepairTab() {
  const state = useDepartmentRepairJobs(JOB_TYPE, REQUEST_PATH);
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <p className="text-sm text-gray-500 mb-5">
        Review startup baseline repair results and safely rerun departments that need attention.
      </p>
      <DepartmentRepairStatusTable
        {...state}
        extraColumns={[
          {
            header: 'Baselines stamped',
            render: (row) => row.job?.payload?.stampedRows == null ? '—' : String(row.job.payload.stampedRows),
          },
          {
            header: 'Rows with no system record',
            render: (row) => row.job?.payload?.orphanedRows == null ? '—' : String(row.job.payload.orphanedRows),
          },
        ]}
      />
    </div>
  );
}
